import { Controller } from '@hotwired/stimulus';
import {
  debounce,
  eventListener,
  id,
  observer,
} from '@inked/utilities';
import Mustache from 'mustache';
import Swiper from 'swiper';
import {
  Autoplay,
  Pagination,
  Zoom,
} from 'swiper/modules';

export class SlideshowController extends Controller {
  static targets = [
    'indicator',
    'indicators',
    'indicatorTemplate',
    'slide',
    'track',
  ];
  static values = {
    autoplay: {
      default: false,
      type: Boolean,
    },
    current: {
      default: 0,
      type: Number,
    },
    loop: {
      default: false,
      type: Boolean,
    },
    maxZoomRatio: {
      default: 3,
      type: Number,
    },
    minZoomRatio: {
      default: 1,
      type: Number,
    },
    spaceBetween: Number,
    triggerAttribute: {
      default: 'data-slideshow-index',
      type: String,
    },
    triggerSelector: String,
    zoom: {
      default: false,
      type: Boolean,
    },
  };

  connect() {
    this.slideshow = new Swiper(this.element, {
      autoplay:
        this.autoplayValue
          ? {
            delay: 5000,
            pauseOnMouseEnter: true,
            disableOnInteraction: true,
          }
          : false,
      focusableElements: 'button',
      loop: this.loopValue,
      modules: [
        ... this.autoplayValue ? [Autoplay] : [],
        ... this.hasIndicatorsTarget ? [Pagination] : [],
        ... this.zoomValue ? [Zoom] : [],
      ],
      on: {
        destroy: () => {
          if (this.triggerSelectorValue) {
            this.stopTriggerSelectorClickEvents();
            this.stopTriggerSelectorObserver();
          }
          if (this.zoomValue) this.stopSlideWheelEvents();
        },
        init: swiper => {
          if (this.triggerSelectorValue) {
            this.stopTriggerSelectorClickEvents = eventListener(
              this.triggerSelectorValue,
              'click',
              event => this.onTriggerSelectorClick(event),
            );

            this.stopTriggerSelectorObserver = observer(
              document.body,
              'mutation',
              mutations => {
                for (const mutation of mutations) {
                  for (const node of mutation.addedNodes) {
                    if (node.nodeType !== Node.ELEMENT_NODE) continue;

                    if (node.matches(this.triggerSelectorValue)) {
                      eventListener(node, 'click', event => this.onTriggerSelectorClick(event));

                      continue;
                    }

                    [...node.querySelectorAll(this.triggerSelectorValue)].forEach(childNode => {
                      eventListener(childNode, 'click', event => this.onTriggerSelectorClick(event));
                    });
                  }
                }
              },
              {
                childList: true,
                subtree: true,
              },
            );
          }

          if (this.zoomValue) {
            this.stopSlideWheelEvents = eventListener(
              this.slideTargets,
              'wheel',
              debounce(event => {
                event.preventDefault();

                const ratio = event.deltaY < 0
                  ? swiper.zoom.scale + 0.25
                  : swiper.zoom.scale - 0.25;

                swiper.zoom.in(Math.max(this.minZoomRatioValue, Math.min(this.maxZoomRatioValue, ratio)));
              }, 10),
            );
          }
        },
        realIndexChange: swiper => this.currentValue = swiper.realIndex,
        zoomChange: (_swiper, scale, _target, container) => {
          if (scale > 1) return container.style.cursor = 'move';

          container.style.cursor = null;
        },
      },
      pagination: this.hasIndicatorsTarget
        ? {
          el: this.indicatorsTarget,
          renderBullet: index => Mustache.render(this.indicatorTemplateTarget.innerHTML, {
            current: this.currentValue == index,
          }),
        }
        : undefined,
      slideClass: (() => {
        const className = `slideshow__slide-${id()}`;

        this.slideTargets.forEach(slide => slide.classList.add(className));

        return className;
      })(),
      spaceBetween: this.spaceBetweenValue,
      wrapperClass: (() => {
        const className = `slideshow__track-${id()}`;

        this.trackTarget.classList.add(className);

        return className;
      })(),
      zoom: this.zoomValue
        ? {
          containerClass: (() => {
            const className = `slideshow__slide-zoom-${id()}`;

            this.slideTargets.forEach(slide => {
              const container = slide.children[0];

              container.classList.add(className);

              if ([ 'img', 'picture', 'canvas' ].includes(container.children[0].tagName.toLowerCase())) return;

              container.children[0].classList.add('swiper-zoom-target');
            });

            return className;
          })(),
          maxRatio: this.maxZoomRatioValue,
          minRatio: this.minZoomRatioValue,
        }
        : undefined,
    });
  }

  currentValueChanged(value) {
    this.slideTargets.forEach((target, targetIndex) => {
      const active = Number(target.dataset.swiperSlideIndex ?? targetIndex) === value;

      target.ariaHidden = !active;
      target.inert = !active;
    });

    if (this.hasIndicatorTarget) {
      this.indicatorTargets.forEach((target, targetIndex) => target.ariaCurrent = targetIndex === value);
    }
  }

  disconnect() {
    this.slideshow.destroy();
  }

  go(event, speed = null) {
    const index = event.params?.index || event.currentTarget.getAttribute(this.triggerAttributeValue) || 0;

    this.loopValue
      ? this.slideshow.slideToLoop(index, speed)
      : this.slideshow.slideTo(index, speed);
  }

  next() {
    this.slideshow.slideNext();
  }

  onTriggerSelectorClick(event) {
    this.go(event, 0);
  }

  previous() {
    this.slideshow.slidePrev();
  }
}
